import React, { useEffect, useState } from 'react';
import { useSettingsStore } from '../../stores/settingsStore';
import { useSessionStore } from '../../stores/sessionStore';
import { apiGet } from '../../lib/api-client';
import type { FilterSettings, StakeLevel, PositionFilter } from '@range-reader/shared';

type DataSource = 'default' | 'user' | 'both';
type TableType = '6' | '9' | 'any';

interface FilterState {
  dataSource: DataSource;
  stakeLevel: 'micro' | 'low' | 'mid' | 'high' | 'custom' | 'any';
  customBigBlindMin?: number;
  customBigBlindMax?: number;
  tableType: TableType;
  dateFrom?: string;
  dateTo?: string;
  playerName?: string;
  position?: PositionFilter;
}

const POSITION_OPTIONS: { value: PositionFilter; label: string }[] = [
  { value: 'any', label: 'Any Position' },
  { value: 'BTN', label: 'Button (BTN)' },
  { value: 'CO', label: 'Cutoff (CO)' },
  { value: 'HJ', label: 'Hijack (HJ)' },
  { value: 'MP', label: 'Middle Position (MP)' },
  { value: 'MP+1', label: 'MP+1' },
  { value: 'UTG+1', label: 'UTG+1' },
  { value: 'UTG', label: 'Under the Gun (UTG)' },
  { value: 'BB', label: 'Big Blind (BB)' },
  { value: 'SB', label: 'Small Blind (SB)' },
];

const DEFAULT_FILTERS: FilterState = {
  dataSource: 'both',
  stakeLevel: 'any',
  tableType: 'any',
};

const STAKE_LABELS: Record<string, string> = {
  any: 'Any',
  micro: 'Micro (NL2–NL10)',
  low: 'Low (NL25–NL50)',
  mid: 'Mid (NL100–NL200)',
  high: 'High (NL500+)',
  custom: 'Custom BB range',
};

export function SessionStartScreen({ onStartSession }: { onStartSession: () => void }) {
  const { settings, update } = useSettingsStore();
  const { startSession } = useSessionStore();

  const [filters, setFilters] = useState<FilterState>(() => {
    try {
      const saved = localStorage.getItem('rr:session-filters');
      if (saved) return JSON.parse(saved) as FilterState;
    } catch { /* ignore */ }
    return DEFAULT_FILTERS;
  });
  const [eligibleCount, setEligibleCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [anonymize, setAnonymize] = useState(settings?.anonymizeDefault ?? false);
  const [matrixDefault, setMatrixDefault] = useState<'full' | 'blank'>(settings?.matrixDefault ?? 'full');
  const [timerEnabled, setTimerEnabled] = useState(() => {
    try { return localStorage.getItem('rr:timer-enabled') === '1'; } catch { return false; }
  });
  const [timerSeconds, setTimerSeconds] = useState(() => {
    try { return parseInt(localStorage.getItem('rr:timer-seconds') ?? '30', 10); } catch { return 30; }
  });
  const [dbStats, setDbStats] = useState<{ defaultCount: number; userCount: number } | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    apiGet<{ defaultHandCount: number; userHandCount: number }>('/api/stats/db').then((s) => {
      setDbStats({ defaultCount: s.defaultHandCount, userCount: s.userHandCount });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const filterPayload = buildFilterPayload(filters);
    apiGet<{ count: number }>('/api/hands/count', { filters: JSON.stringify(filterPayload) })
      .then((res) => setEligibleCount(res.count))
      .catch(() => setEligibleCount(0))
      .finally(() => setLoading(false));
  }, [filters]);

  function buildFilterPayload(f: FilterState): FilterSettings {
    return {
      dataSource: f.dataSource,
      stakeLevel: f.stakeLevel as StakeLevel,
      customBBMin: f.stakeLevel === 'custom' ? (f.customBigBlindMin ?? null) : null,
      customBBMax: f.stakeLevel === 'custom' ? (f.customBigBlindMax ?? null) : null,
      tableType: f.tableType === 'any' ? 'any' : (f.tableType === '6' ? '6max' : '9max'),
      dateFrom: f.dateFrom ? Math.floor(new Date(f.dateFrom).getTime() / 1000) : null,
      dateTo: f.dateTo ? Math.floor(new Date(f.dateTo).getTime() / 1000) : null,
      playerName: f.playerName || null,
      position: f.position && f.position !== 'any' ? f.position : null,
    };
  }

  async function handleStart(useFilters: boolean) {
    await update({ anonymizeDefault: anonymize, matrixDefault });
    try {
      localStorage.setItem('rr:timer-enabled', timerEnabled ? '1' : '0');
      localStorage.setItem('rr:timer-seconds', String(timerSeconds));
    } catch { /* ignore */ }
    const filterPayload = useFilters
      ? buildFilterPayload(filters)
      : buildFilterPayload(DEFAULT_FILTERS);
    await startSession(filterPayload, matrixDefault);
    onStartSession();
  }

  function setFilter<K extends keyof FilterState>(key: K, value: FilterState[K]) {
    setFilters(prev => {
      const next = { ...prev, [key]: value };
      try { localStorage.setItem('rr:session-filters', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }

  const totalHands = dbStats ? dbStats.defaultCount + dbStats.userCount : null;
  const canStart = eligibleCount !== null && eligibleCount > 0 && !loading;
  const hasActiveFilters = filters.stakeLevel !== 'any' || filters.tableType !== 'any' ||
    filters.dataSource !== 'both' || filters.dateFrom || filters.dateTo || filters.playerName ||
    (filters.position && filters.position !== 'any');

  return (
    <div className="session-start-screen">
      <div className="session-start-center">
        <h1 className="session-start-title">Range Reader</h1>
        <p className="session-start-subtitle">Texas Hold'em Hand Reading Training</p>

        {/* Database stats */}
        {dbStats && (
          <div className="db-stats-card">
            <div className="db-stat">
              <span className="db-stat-label">Default DB</span>
              <span className="db-stat-value">{dbStats.defaultCount.toLocaleString()} hands</span>
            </div>
            <div className="db-stat">
              <span className="db-stat-label">My Hands</span>
              <span className="db-stat-value">{dbStats.userCount.toLocaleString()} hands</span>
            </div>
          </div>
        )}

        {/* Session settings */}
        <div className="session-settings-card">
          <h3>Session Settings</h3>
          <label className="toggle-row">
            <span>Anonymize Players</span>
            <input type="checkbox" checked={anonymize} onChange={e => setAnonymize(e.target.checked)} />
          </label>
          <div className="radio-row">
            <span>Matrix Start</span>
            <label>
              <input type="radio" name="matrix" value="full" checked={matrixDefault === 'full'} onChange={() => setMatrixDefault('full')} />
              Full range
            </label>
            <label>
              <input type="radio" name="matrix" value="blank" checked={matrixDefault === 'blank'} onChange={() => setMatrixDefault('blank')} />
              Blank
            </label>
          </div>
          <label className="toggle-row">
            <span>Timed Mode</span>
            <input type="checkbox" checked={timerEnabled} onChange={e => setTimerEnabled(e.target.checked)} />
          </label>
          {timerEnabled && (
            <div className="timer-setting-row">
              <span className="timer-setting-label">Seconds per decision</span>
              <div className="timer-presets">
                {[15, 30, 45, 60].map(s => (
                  <button
                    key={s}
                    type="button"
                    className={`timer-preset-btn ${timerSeconds === s ? 'active' : ''}`}
                    onClick={() => setTimerSeconds(s)}
                  >
                    {s}s
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Quick Start */}
        <div className="start-section">
          <button
            className="btn-start-session btn-quick-start"
            onClick={() => handleStart(false)}
            disabled={totalHands === 0}
          >
            ▶ Quick Start
            {totalHands !== null && <span className="btn-subtitle">{totalHands.toLocaleString()} hands available</span>}
          </button>

          {/* Filter toggle */}
          <button
            className={`btn-toggle-filters ${showFilters ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
            onClick={() => setShowFilters(v => !v)}
          >
            {showFilters ? '▲ Hide Filters' : '▼ Apply Filters'}
            {hasActiveFilters && !showFilters && <span className="filter-badge">●</span>}
          </button>
        </div>

        {/* Collapsible filter panel */}
        {showFilters && (
          <div className="filter-panel">
            <div className="filter-panel-header">
              <h3>Filters</h3>
              <button className="btn-clear-filters" onClick={() => setFilters(DEFAULT_FILTERS)}>
                Clear All
              </button>
            </div>

            {/* Data source */}
            <div className="filter-group">
              <label className="filter-label">Data Source</label>
              <div className="filter-radio-group">
                {(['both', 'default', 'user'] as const).map(src => (
                  <label key={src} className="filter-radio">
                    <input type="radio" name="dataSource" value={src}
                      checked={filters.dataSource === src}
                      onChange={() => setFilter('dataSource', src)} />
                    {src === 'both' ? 'Both' : src === 'default' ? 'Default DB' : 'My Hands'}
                  </label>
                ))}
              </div>
            </div>

            {/* Stake level */}
            <div className="filter-group">
              <label className="filter-label">Stake Level</label>
              <select className="filter-select" value={filters.stakeLevel}
                onChange={e => setFilter('stakeLevel', e.target.value as FilterState['stakeLevel'])}>
                {Object.entries(STAKE_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
              {filters.stakeLevel === 'custom' && (
                <div className="custom-bb-range">
                  <input type="number" placeholder="Min BB (¢)"
                    value={filters.customBigBlindMin ?? ''}
                    onChange={e => setFilter('customBigBlindMin', parseInt(e.target.value) || undefined)} />
                  <span>—</span>
                  <input type="number" placeholder="Max BB (¢)"
                    value={filters.customBigBlindMax ?? ''}
                    onChange={e => setFilter('customBigBlindMax', parseInt(e.target.value) || undefined)} />
                </div>
              )}
            </div>

            {/* Table type */}
            <div className="filter-group">
              <label className="filter-label">Table Type</label>
              <div className="filter-radio-group">
                {([['any', 'Any'], ['6', '6-max'], ['9', '9-max']] as const).map(([val, label]) => (
                  <label key={val} className="filter-radio">
                    <input type="radio" name="tableType" value={val}
                      checked={filters.tableType === val}
                      onChange={() => setFilter('tableType', val)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Hero position */}
            <div className="filter-group">
              <label className="filter-label">Hero Position</label>
              <select className="filter-select" value={filters.position ?? 'any'}
                onChange={e => setFilter('position', e.target.value === 'any' ? undefined : e.target.value as PositionFilter)}>
                {POSITION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Date range */}
            <div className="filter-group">
              <label className="filter-label">Date Range</label>
              <div className="date-range-row">
                <input type="date" className="filter-input" value={filters.dateFrom ?? ''}
                  onChange={e => setFilter('dateFrom', e.target.value || undefined)} />
                <span>to</span>
                <input type="date" className="filter-input" value={filters.dateTo ?? ''}
                  onChange={e => setFilter('dateTo', e.target.value || undefined)} />
              </div>
            </div>

            {/* Player name */}
            <div className="filter-group">
              <label className="filter-label">Player Name</label>
              <input type="text" className="filter-input" placeholder="Filter by player name..."
                value={filters.playerName ?? ''}
                onChange={e => setFilter('playerName', e.target.value || undefined)} />
            </div>

            {/* Filtered start */}
            <div className="filter-start-row">
              {loading ? (
                <span className="eligible-count loading">Counting...</span>
              ) : eligibleCount !== null ? (
                <span className={`eligible-count ${eligibleCount === 0 ? 'zero' : ''}`}>
                  {eligibleCount === 0
                    ? 'No hands match — try broadening your filters'
                    : `${eligibleCount.toLocaleString()} hands match`}
                </span>
              ) : null}
              <button
                className="btn-start-session btn-filtered-start"
                onClick={() => handleStart(true)}
                disabled={!canStart}
              >
                Start with Filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
